import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";

import { api } from "../api/client";
import type {
  HindsightCall,
  HindsightHorizon,
  HindsightStats,
  PinnedRec,
  TrendingHindsight,
  TuneRow,
} from "../api/types";
import { useMe } from "../api/me";
import Footer from "../components/Footer";
import PageHeader from "../components/PageHeader";
import StrategyTab from "../components/hindsight/StrategyTab";
import TopNav from "../components/TopNav";
import { echarts, PALETTE, baseAxis, baseTooltip } from "../charts/echarts";
import { isAdmin } from "../lib/admin";

type SubTab = "recommendations" | "trending" | "pinned" | "strategy";
type Order = "best" | "worst" | "recent";

const HORIZONS: { id: HindsightHorizon; label: string }[] = [
  { id: "intraday", label: "Intraday" },
  { id: "short",    label: "Short" },
  { id: "medium",   label: "Medium" },
  { id: "long",     label: "Long" },
  { id: "all",      label: "All" },
];

const INTRADAY_NOTE =
  "Intraday is experimental — Yahoo data has 5–15 min delay and we don't see " +
  "order flow. Use it to validate whether short-horizon calls are calibrated, " +
  "not as a day-trading signal.";

function pct(v: number | null | undefined): string {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-2xl tabular-nums text-slate-100">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

function CalibrationChart({ stats }: { stats: HindsightStats }) {
  const cal = stats.calibration ?? [];
  if (cal.length === 0) {
    return <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-sm text-slate-500">
      No matured calls yet for this horizon. Calibration appears once the maturity window elapses.
    </div>;
  }
  const labels = cal.map((b) => `${b.stance === "bullish" ? "↑" : "↓"} ${b.score_lo}–${b.score_hi >= 100 ? 100 : b.score_hi}`);
  const values = cal.map((b) => Math.round(b.hit_rate * 100));
  const counts = cal.map((b) => b.count);
  const colors = cal.map((b) => b.stance === "bullish" ? PALETTE.bull : PALETTE.bear);
  const option = {
    backgroundColor: "transparent",
    animation: false,
    tooltip: {
      ...baseTooltip,
      formatter: (p: any) => {
        const i = p[0].dataIndex;
        return `${labels[i]}<br/>hit rate: ${values[i]}%<br/>n=${counts[i]}`;
      },
    },
    grid: { left: 44, right: 16, top: 16, bottom: 56 },
    xAxis: {
      ...baseAxis,
      type: "category",
      data: labels,
      axisLabel: { ...baseAxis.axisLabel, rotate: 30 },
    },
    yAxis: {
      ...baseAxis,
      type: "value",
      min: 0,
      max: 100,
      axisLabel: { ...baseAxis.axisLabel, formatter: "{value}%" },
    },
    series: [
      {
        type: "bar",
        data: values.map((v, i) => ({ value: v, itemStyle: { color: colors[i] } })),
        barWidth: "70%",
        label: { show: true, position: "top", color: PALETTE.muted, fontSize: 10, formatter: "{c}%" },
      },
    ],
  } satisfies echarts.EChartsCoreOption;
  return <ReactECharts option={option} style={{ height: 240, width: "100%" }} echarts={echarts} notMerge />;
}

function CallsTable({ calls }: { calls: HindsightCall[] }) {
  if (!calls.length) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-800">
      <table className="w-full text-left text-[12px]">
        <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2">Stance</th>
            <th className="px-3 py-2 text-right">Score</th>
            <th className="px-3 py-2 text-right">Raw</th>
            <th className="px-3 py-2 text-right">SPY</th>
            <th className="px-3 py-2 text-right">Excess</th>
            <th className="px-3 py-2">Result</th>
            <th className="px-3 py-2">Generated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {calls.map((c) => (
            <tr key={c.rec_id} className="bg-slate-950">
              <td className="px-3 py-2">
                <div className="font-medium text-slate-100">{c.yahoo_symbol}</div>
                <div className="text-[10px] text-slate-500">{c.name ?? ""} · {c.horizon}</div>
              </td>
              <td className="px-3 py-2 text-slate-300">{c.stance}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{c.score.toFixed(0)}</td>
              <td className={`px-3 py-2 text-right font-mono tabular-nums ${(c.raw_return ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{pct(c.raw_return)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">{pct(c.spy_return)}</td>
              <td className={`px-3 py-2 text-right font-mono tabular-nums ${(c.excess_return ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{pct(c.excess_return)}</td>
              <td className="px-3 py-2">
                <span className={`rounded px-2 py-0.5 text-[10px] font-medium ${c.hit ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                  {c.hit ? "HIT" : "MISS"}
                </span>
              </td>
              <td className="px-3 py-2 text-[10px] text-slate-500">{new Date(c.generated_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecommendationsTab() {
  const me = useMe();
  const showTune = isAdmin(me.data?.email);
  const [horizon, setHorizon] = useState<HindsightHorizon>("intraday");
  const [order, setOrder] = useState<Order>("recent");

  const stats = useQuery<HindsightStats>({
    queryKey: ["hindsight-stats", horizon],
    queryFn: async () => (await api.get<HindsightStats>("/hindsight/stats", { params: { horizon } })).data,
    staleTime: 60_000,
  });
  const calls = useQuery<HindsightCall[]>({
    queryKey: ["hindsight-calls", horizon, order],
    queryFn: async () => (await api.get<HindsightCall[]>("/hindsight/calls", { params: { horizon, order, limit: 20 } })).data,
    staleTime: 60_000,
  });
  const tune = useMutation<TuneRow[]>({
    mutationFn: async () => (await api.post<TuneRow[]>("/hindsight/tune")).data,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-md border border-slate-700 bg-slate-900 p-0.5 text-[11px]">
          {HORIZONS.map((h) => (
            <button
              key={h.id}
              onClick={() => setHorizon(h.id)}
              className={`rounded px-2 py-1 ${horizon === h.id ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"}`}
            >
              {h.label}
            </button>
          ))}
        </div>
        {stats.data?.deadband_pct != null && (
          <div className="text-[11px] text-slate-500">
            hit deadband ±{stats.data.deadband_pct}% over {horizon === "intraday" ? 1 : horizon === "short" ? 7 : horizon === "medium" ? 30 : 180} trading days
          </div>
        )}
      </div>

      {horizon === "intraday" && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-[11px] leading-relaxed text-amber-200">
          {INTRADAY_NOTE}
        </div>
      )}

      {stats.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Calls" value={stats.data?.count?.toLocaleString() ?? "0"} />
            <StatCard label="Hit rate" value={stats.data?.count ? `${Math.round((stats.data.hit_rate || 0) * 100)}%` : "—"} />
            <StatCard label="Avg return" value={pct(stats.data?.avg_raw_return ?? null)} />
            <StatCard label="Avg vs SPY" value={pct(stats.data?.avg_excess_return ?? null)} />
          </div>

          <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
            <header className="mb-2 flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-slate-300">Calibration</div>
              <div className="flex items-center gap-3 text-[10px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-sm" style={{ background: PALETTE.bull }} />
                  ↑ bullish
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-sm" style={{ background: PALETTE.bear }} />
                  ↓ bearish
                </span>
              </div>
            </header>
            <p className="mb-3 text-[11px] leading-relaxed text-slate-500">
              Hit rate of matured calls grouped by score band. Score ≥ 50 is a bullish call (↑), &lt; 50 is bearish (↓);
              the further from 50, the higher the model's confidence. A well-calibrated score rises monotonically: ↑ bars
              should grow left-to-right, ↓ bars should grow right-to-left. Flat or inverted bars mean the score isn't
              predictive at this horizon.
            </p>
            {stats.data && <CalibrationChart stats={stats.data} />}
          </section>

          <section className="space-y-2">
            <header className="flex items-center justify-between">
              <div className="text-sm font-medium text-slate-300">Calls</div>
              <div className="flex gap-1 rounded-md border border-slate-700 bg-slate-900 p-0.5 text-[11px]">
                {(["recent", "best", "worst"] as Order[]).map((o) => (
                  <button
                    key={o}
                    onClick={() => setOrder(o)}
                    className={`rounded px-2 py-1 ${order === o ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"}`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </header>
            {calls.isLoading ? (
              <div className="text-sm text-slate-500">Loading…</div>
            ) : (calls.data?.length ?? 0) === 0 ? (
              <div className="text-sm text-slate-500">No matured calls yet.</div>
            ) : (
              <CallsTable calls={calls.data ?? []} />
            )}
          </section>

          {showTune && (
          <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
            <header className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-300">Tune weights</div>
                <div className="text-[11px] text-slate-500">Grid-search WEIGHT_LLM / WEIGHT_MOMENTUM / WEIGHT_SENTIMENT on past matured calls. Read-only suggestion — env vars stay as-is.</div>
              </div>
              <button
                onClick={() => tune.mutate()}
                className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1 text-[11px] text-slate-200 hover:bg-slate-700 disabled:opacity-50"
                disabled={tune.isPending}
              >
                {tune.isPending ? "Running…" : "Run tune"}
              </button>
            </header>
            {tune.data && tune.data.length > 0 && (
              <div className="overflow-hidden rounded-md border border-slate-800">
                <table className="w-full text-left text-[12px]">
                  <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-1.5">w_llm</th>
                      <th className="px-3 py-1.5">w_mom</th>
                      <th className="px-3 py-1.5">w_sent</th>
                      <th className="px-3 py-1.5 text-right">hit rate</th>
                      <th className="px-3 py-1.5 text-right">avg excess</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {tune.data.slice(0, 10).map((r, i) => (
                      <tr key={i} className="bg-slate-950 font-mono tabular-nums">
                        <td className="px-3 py-1.5">{r.w_llm}</td>
                        <td className="px-3 py-1.5">{r.w_momentum}</td>
                        <td className="px-3 py-1.5">{r.w_sentiment}</td>
                        <td className="px-3 py-1.5 text-right">{Math.round(r.hit_rate * 100)}%</td>
                        <td className={`px-3 py-1.5 text-right ${(r.avg_excess_return ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{pct(r.avg_excess_return)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tune.data && tune.data.length === 0 && (
              <div className="text-sm text-slate-500">Need at least 5 matured calls before tuning is meaningful.</div>
            )}
          </section>
          )}
        </>
      )}
    </div>
  );
}

function TrendingTab() {
  const [horizon, setHorizon] = useState<"short" | "medium" | "long">("short");
  const t = useQuery<TrendingHindsight>({
    queryKey: ["trending-hindsight", horizon],
    queryFn: async () => (await api.get<TrendingHindsight>("/hindsight/trending", { params: { horizon } })).data,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-md border border-slate-700 bg-slate-900 p-0.5 text-[11px]">
          {(["short", "medium", "long"] as const).map((h) => (
            <button
              key={h}
              onClick={() => setHorizon(h)}
              className={`rounded px-2 py-1 ${horizon === h ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"}`}
            >
              {h}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-slate-500">
          Did trending tickers actually outperform SPY? Buckets group by aggregate score.
        </div>
      </div>

      {t.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (t.data?.count ?? 0) === 0 ? (
        <div className="text-sm text-slate-500">
          No matured trending snapshots yet. Each snapshot needs the {horizon === "short" ? 7 : horizon === "medium" ? 30 : 180}-trading-day window to elapse.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Snapshots" value={t.data?.count.toLocaleString() ?? "0"} />
            <StatCard label="Win rate (excess > 0)" value={`${Math.round((t.data?.win_rate ?? 0) * 100)}%`} />
            <StatCard label="Avg excess" value={pct(t.data?.avg_excess_return ?? null)} />
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-800">
            <table className="w-full text-left text-[12px]">
              <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Score band</th>
                  <th className="px-3 py-2 text-right">n</th>
                  <th className="px-3 py-2 text-right">avg excess</th>
                  <th className="px-3 py-2 text-right">win rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {t.data?.buckets.map((b, i) => (
                  <tr key={i} className="bg-slate-950 font-mono tabular-nums">
                    <td className="px-3 py-2">{Number.isFinite(b.score_lo) ? b.score_lo : "<"}{Number.isFinite(b.score_hi) ? `–${b.score_hi}` : "+"}</td>
                    <td className="px-3 py-2 text-right">{b.count}</td>
                    <td className={`px-3 py-2 text-right ${(b.avg_excess_return ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{pct(b.avg_excess_return)}</td>
                    <td className="px-3 py-2 text-right">{Math.round(b.win_rate * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function PinnedTab() {
  const qc = useQueryClient();
  const pinned = useQuery<PinnedRec[]>({
    queryKey: ["pinned"],
    queryFn: async () => (await api.get<PinnedRec[]>("/pinned")).data,
  });
  const unpin = useMutation({
    mutationFn: async (id: number) => api.delete(`/pinned/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pinned"] }),
  });

  const rows = pinned.data ?? [];
  if (pinned.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (rows.length === 0)
    return (
      <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 text-sm text-slate-500">
        Nothing pinned yet. Open any ticker's detail page and click <span className="text-slate-300">Pin</span> on a horizon
        score card to start tracking it here.
      </div>
    );

  const cls = (v: number | null) => v === null ? "text-slate-500" : v >= 0 ? "text-emerald-400" : "text-rose-400";

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
      <table className="w-full text-sm">
        <thead className="bg-slate-950 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2">Ticker</th>
            <th className="px-4 py-2">Stance · horizon</th>
            <th className="px-4 py-2 text-right">Entry</th>
            <th className="px-4 py-2 text-right">Now</th>
            <th className="px-4 py-2 text-right">Raw %</th>
            <th className="px-4 py-2 text-right">SPY %</th>
            <th className="px-4 py-2 text-right">Excess</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-slate-800">
              <td className="px-4 py-2.5">
                <Link to={`/s/${encodeURIComponent(p.yahoo_symbol)}`} className="font-mono text-slate-100 hover:text-sky-300">
                  {p.ticker}
                </Link>
                <div className="text-[11px] text-slate-500">{new Date(p.pinned_at).toLocaleDateString()}</div>
              </td>
              <td className="px-4 py-2.5">
                <span className={p.stance === "bullish" ? "text-emerald-400" : p.stance === "bearish" ? "text-rose-400" : "text-slate-400"}>
                  {p.stance}
                </span>
                <span className="ml-1 text-[11px] text-slate-500">· {p.horizon}</span>
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{p.entry_price?.toFixed(2) ?? "—"}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{p.last_price?.toFixed(2) ?? "—"}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums ${cls(p.raw_return_pct)}`}>{p.raw_return_pct === null ? "—" : `${p.raw_return_pct >= 0 ? "+" : ""}${p.raw_return_pct.toFixed(2)}%`}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums ${cls(p.spy_return_pct)}`}>{p.spy_return_pct === null ? "—" : `${p.spy_return_pct >= 0 ? "+" : ""}${p.spy_return_pct.toFixed(2)}%`}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums ${cls(p.excess_return_pct)}`}>{p.excess_return_pct === null ? "—" : `${p.excess_return_pct >= 0 ? "+" : ""}${p.excess_return_pct.toFixed(2)}%`}</td>
              <td className="px-2 py-2.5 text-right">
                <button
                  onClick={() => unpin.mutate(p.id)}
                  className="rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-800 hover:text-rose-400"
                  title="Unpin"
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function Hindsight() {
  const [params, setParams] = useSearchParams();
  const qTab = params.get("tab");
  const initialTab: SubTab =
    qTab === "trending" || qTab === "pinned" || qTab === "strategy"
      ? (qTab as SubTab)
      : "recommendations";
  const [tab, setTabState] = useState<SubTab>(initialTab);

  function setTab(next: SubTab) {
    setTabState(next);
    const np = new URLSearchParams(params);
    if (next === "recommendations") np.delete("tab");
    else np.set("tab", next);
    setParams(np, { replace: true });
  }

  return (
    <div className="mx-auto w-full max-w-5xl p-4 sm:p-6">
      <PageHeader />
      <TopNav />
      <header className="mb-4">
        <h1 className="text-lg font-semibold text-slate-100">Hindsight</h1>
        <p className="text-xs text-slate-500">
          Track-record on past calls — were the analysis stances right? Maturity windows: short=7, medium=30, long=180 trading days.
        </p>
      </header>

      <div className="mb-3 -mx-1 flex gap-1 overflow-x-auto px-1 pb-1 sm:mb-4">
        {([
          { id: "recommendations" as const, label: "Recommendations" },
          { id: "trending" as const, label: "Trending vs SPY" },
          { id: "pinned" as const, label: "My pinned" },
          { id: "strategy" as const, label: "Strategy" },
        ]).map((s) => (
          <button
            key={s.id}
            onClick={() => setTab(s.id)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              tab === s.id
                ? "bg-slate-800 text-slate-100 ring-1 ring-slate-700"
                : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {tab === "recommendations" && <RecommendationsTab />}
      {tab === "trending" && <TrendingTab />}
      {tab === "pinned" && <PinnedTab />}
      {tab === "strategy" && <StrategyTab />}

      <Footer note="Maturity windows: short=7d, medium=30d, long=180d trading days · Source: yfinance closes" />
    </div>
  );
}
